// src/components/layout/DashboardLayout.jsx
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAuthStore from '@/store/authStore'
import ErrorBoundary from '@/components/common/ErrorBoundary'
import {
  LayoutDashboard, Users, CreditCard, Globe,
  ClipboardList, LogOut, Menu, X, Bell,
  GraduationCap, ChevronDown, Settings, Receipt,
  TrendingUp, MessageSquare, BookOpen, FileText, Bookmark,
  ClipboardCheck, Target, BarChart3, School, Bot, HelpCircle,
  Zap, Trophy, Calendar, PenSquare, Award, User, Upload,
} from 'lucide-react'
import clsx from 'clsx'
import NotificationBell from '@/components/notifications/NotificationBell'
import LanguageSwitcher from '@/components/common/LanguageSwitcher'

const NAV_ITEMS = {
  admin: [
    { to: '/admin/dashboard', labelKey: 'nav.dashboard',          icon: LayoutDashboard },
    { to: '/admin/insights',  labelKey: 'nav.insights',           icon: TrendingUp },
    { to: '/builder',         labelKey: 'nav.websiteBuilder',     icon: Globe },
    { to: '/admissions',      labelKey: 'nav.admissions',         icon: ClipboardList },
    { to: '/admin/classes',   labelKey: 'nav.classes',            icon: School },
    { to: '/admin/students',  labelKey: 'nav.students',           icon: Users },
    { to: '/admin/notifications', labelKey: 'nav.sendNotifications', icon: Bell },
    { to: '/notifications',       labelKey: 'nav.notificationCenter', icon: Bell },
    { to: '/admin/reminders',     labelKey: 'nav.reminders',      icon: ClipboardCheck },
    { to: '/fees',            labelKey: 'nav.fees',               icon: CreditCard },
    { to: '/admin/fee-reports', labelKey: 'nav.reports',          icon: Receipt },
    { to: '/admin/subjects',  labelKey: 'nav.content',           icon: BookOpen },
    { to: '/admin/papers',    labelKey: 'nav.papers',            icon: FileText },
    { to: '/admin/import',    labelKey: 'nav.importContent',     icon: Upload },
    { to: '/admin/analytics',  labelKey: 'nav.analytics',        icon: BarChart3 },
    { to: '/admin/question-banks', labelKey: 'nav.questionBanks', icon: ClipboardCheck },
    { to: '/admin/practice-tests', labelKey: 'nav.practiceTests', icon: Target },
    { to: '/ai-tutor',        labelKey: 'nav.aiTutor',           icon: Bot },
    { to: '/ai/doubts',       labelKey: 'nav.doubtSolver',       icon: HelpCircle },
    { to: '/quizzes',         labelKey: 'nav.liveQuizzes',       icon: Zap },
    { to: '/leaderboard',    labelKey: 'nav.leaderboard',        icon: Trophy },
    { to: '/qa',             labelKey: 'nav.qaForum',            icon: HelpCircle },
    { to: '/discussions',      labelKey: 'nav.discussions',       icon: MessageSquare },
    { to: '/content',         labelKey: 'nav.browseContent',     icon: BookOpen },
    { to: '/bookmarks',       labelKey: 'nav.bookmarks',         icon: Bookmark },
    { to: '/billing',         labelKey: 'nav.billing',           icon: Settings },
  ],
  teacher: [
    { to: '/teacher/dashboard', labelKey: 'nav.dashboard',       icon: LayoutDashboard },
    { to: '/notifications',     labelKey: 'nav.notifications',   icon: Bell },
    { to: '/messages',          labelKey: 'nav.messages',        icon: MessageSquare },
    { to: '/admin/students',    labelKey: 'nav.myClass',         icon: Users },
    { to: '/discussions',          labelKey: 'nav.discussions',   icon: MessageSquare },
    { to: '/admin/analytics/class/0', labelKey: 'nav.performance', icon: BarChart3 },
    { to: '/admin/question-banks', labelKey: 'nav.questionBanks', icon: ClipboardCheck },
    { to: '/admin/practice-tests', labelKey: 'nav.practiceTests', icon: Target },
    { to: '/quizzes',           labelKey: 'nav.liveQuizzes',     icon: Zap },
    { to: '/leaderboard',       labelKey: 'nav.leaderboard',     icon: Trophy },
    { to: '/qa',                labelKey: 'nav.qaForum',         icon: HelpCircle },
    { to: '/ai-tutor',          labelKey: 'nav.aiTutor',         icon: Bot },
    { to: '/ai/doubts',          labelKey: 'nav.doubtSolver',    icon: HelpCircle },
    { to: '/content',           labelKey: 'nav.content',         icon: BookOpen },
    { to: '/bookmarks',         labelKey: 'nav.bookmarks',       icon: Bookmark },
  ],
  parent: [
    { to: '/parent/dashboard', labelKey: 'nav.dashboard',        icon: LayoutDashboard },
    { to: '/notifications',    labelKey: 'nav.notifications',    icon: Bell },
    { to: '/messages',         labelKey: 'nav.messages',         icon: MessageSquare },
    { to: '/classroom/join',   labelKey: 'nav.joinClass',        icon: School },
    { to: '/discussions',      labelKey: 'nav.discussions',       icon: MessageSquare },
    { to: '/analytics/student/0', labelKey: 'nav.performance',   icon: BarChart3 },
    { to: '/practice',         labelKey: 'nav.practiceTests',    icon: ClipboardCheck },
    { to: '/practice/readiness', labelKey: 'nav.examReadiness',  icon: Target },
    { to: '/leaderboard',      labelKey: 'nav.leaderboard',      icon: Trophy },
    { to: '/qa',               labelKey: 'nav.qaForum',          icon: HelpCircle },
    { to: '/ai-tutor',         labelKey: 'nav.aiTutor',          icon: Bot },
    { to: '/ai/doubts',         labelKey: 'nav.doubtSolver',     icon: HelpCircle },
    { to: '/content',          labelKey: 'nav.content',          icon: BookOpen },
    { to: '/bookmarks',        labelKey: 'nav.bookmarks',        icon: Bookmark },
  ],
  student: [
    { to: '/student/dashboard',    labelKey: 'nav.dashboard',       icon: LayoutDashboard },
    { to: '/student/timetable',    labelKey: 'nav.timetable',       icon: Calendar },
    { to: '/student/attendance',   labelKey: 'nav.attendance',      icon: ClipboardCheck },
    { to: '/student/assignments',  labelKey: 'nav.assignments',     icon: PenSquare },
    { to: '/student/exam-results', labelKey: 'nav.examResults',     icon: BarChart3 },
    { to: '/student/report-card',  labelKey: 'nav.reportCard',      icon: FileText },
    { to: '/student/fees',         labelKey: 'nav.fees',            icon: CreditCard },
    { to: '/student/notices',      labelKey: 'nav.notices',         icon: Bell },
    // separator
    { to: '/content',              labelKey: 'nav.contentLibrary',  icon: BookOpen },
    { to: '/practice',             labelKey: 'nav.practiceTests',   icon: Target },
    { to: '/practice/readiness',   labelKey: 'nav.examReadiness',   icon: Award },
    { to: '/ai-tutor',             labelKey: 'nav.aiTutor',         icon: Bot },
    { to: '/ai/doubts',            labelKey: 'nav.doubtSolver',     icon: HelpCircle },
    // separator
    { to: '/discussions',          labelKey: 'nav.discussions',      icon: MessageSquare },
    { to: '/leaderboard',          labelKey: 'nav.leaderboard',     icon: Trophy },
    { to: '/qa',                   labelKey: 'nav.qaForum',         icon: HelpCircle },
    { to: '/notifications',        labelKey: 'nav.notifications',   icon: Bell },
    { to: '/student/profile',      labelKey: 'nav.profile',         icon: User },
  ],
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const { t } = useTranslation()
  const { user, tenant, logout } = useAuthStore()
  const navigate = useNavigate()

  // Super admin gets the admin sidebar
  const navItems = NAV_ITEMS[user?.role === 'super_admin' ? 'admin' : user?.role] || []

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">

      {/* ── SIDEBAR ──────────────────────────────────────────── */}
      <aside className={clsx(
        'flex flex-col bg-[#0d2757] text-white transition-all duration-300 flex-shrink-0',
        sidebarOpen ? 'w-60' : 'w-16'
      )}>

        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-white/10">
          <div className="w-8 h-8 bg-orange-400 rounded-lg flex items-center justify-center flex-shrink-0">
            <GraduationCap size={18} className="text-[#0d2757]" />
          </div>
          {sidebarOpen && (
            <span className="font-bold text-lg tracking-tight">
              educ<span className="text-orange-400">za</span>
            </span>
          )}
        </div>

        {/* School name */}
        {sidebarOpen && tenant && (
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-xs text-white/50 uppercase tracking-wider mb-1">{t('common.school')}</p>
            <p className="text-sm font-medium text-white truncate">{tenant.name}</p>
            <span className={clsx(
              'inline-block text-xs px-2 py-0.5 rounded-full mt-1 font-medium',
              tenant.plan === 'trial' ? 'bg-orange-400/20 text-orange-300' :
              tenant.plan === 'pro'   ? 'bg-purple-400/20 text-purple-300' :
                                       'bg-green-400/20 text-green-300'
            )}>
              {tenant.plan?.toUpperCase()}
            </span>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                isActive
                  ? 'bg-orange-400 text-[#0d2757]'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              )}
            >
              <Icon size={18} className="flex-shrink-0" />
              {sidebarOpen && <span>{t(labelKey)}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User + Logout */}
        <div className="px-2 pb-4 border-t border-white/10 pt-3">
          {sidebarOpen && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-orange-400 flex items-center justify-center text-sm font-bold text-[#0d2757] flex-shrink-0">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-white/50 capitalize">{user?.role}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-red-500/20 hover:text-red-300 transition-all"
          >
            <LogOut size={18} className="flex-shrink-0" />
            {sidebarOpen && <span>{t('auth.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div className="flex items-center gap-4">
            {/* Trial banner */}
            {tenant?.plan === 'trial' && (
              <NavLink
                to="/billing"
                className="text-xs bg-orange-50 border border-orange-200 text-orange-700 px-3 py-1.5 rounded-full font-medium hover:bg-orange-100 transition-colors"
              >
                {t('dashboard.trialBanner')}
              </NavLink>
            )}
            <LanguageSwitcher />
            <NotificationBell />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  )
}
